// Service-worker update flow (issues 293 + 409) — extracted from app.ts the
// way sw-logic.ts was extracted from sw.ts: the bootstrap wires it once, the
// behavior lives here where it can be unit-tested.
//
// A new worker no longer installs itself over a running tab (issue 292), so it
// parks in `waiting` until someone consents. This notices the parked worker,
// asks it which version it offers so the banner can say so (issue 409 — a
// bare "new version is ready" reads as a false alarm on dev, where the footer
// already shows the freshly-fetched shell's version), and reloads this tab
// when a takeover actually happens — whichever tab consented. Without that
// last part, a takeover consented in ANOTHER tab left this one running old
// chunks under the new worker (whose activate just purged their cache: the
// blank-screen hazard issue 292 fixed for the single-tab case) and showing a
// stale banner no reload could clear.

import { UpdatePrompt } from '../components/UpdatePrompt';

// How long the banner waits for the worker's version reply before showing the
// version-less text instead: a waiting worker from a deploy that predates the
// GET_VERSION handler never answers at all.
export const VERSION_REPLY_TIMEOUT_MS = 800;

// Module-level, not per-mount: the prompt lives on document.body, outside the
// AppShell that every route change tears down and rebuilds.
const updatePrompt = new UpdatePrompt();

// controllerchange fires once per takeover, but a hard refresh with devtools'
// "Update on reload" can fire it twice — and reloading on the second one, while
// the first reload is already in flight, is how a reload loop starts (AC.3).
let reloading = false;

// Whether this page is (or has become) controlled. The very first install
// claims the page (sw.ts activate → clients.claim()), and that claim fires
// controllerchange too — a takeover of nothing, needing no reload. Every
// controllerchange AFTER the page is controlled is a real version takeover.
let hadController = false;

// Test hook: the flags and the prompt are module state invisible to suites.
export function resetSwUpdateStateForTests(): void {
  reloading = false;
  hadController = false;
  updatePrompt.hide();
}

// versionOf asks a worker which version it carries (sw.ts GET_VERSION),
// resolving null when it does not answer in time — an older worker has no
// handler for the message and stays silent.
function versionOf(worker: ServiceWorker, timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent) => {
      window.clearTimeout(timer);
      const version = (event.data as { version?: unknown } | null)?.version;
      resolve(typeof version === 'string' && version !== '' ? version : null);
    };
    try {
      worker.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
    } catch {
      window.clearTimeout(timer);
      resolve(null);
    }
  });
}

function takeOver(reg: ServiceWorkerRegistration, offered: ServiceWorker): void {
  // Reload happens when the new worker actually takes control — via the
  // module-level controllerchange listener below, which serves every tab, not
  // just the one that clicked. Reloading first would just re-open the page
  // under the OLD controller and show the prompt again.
  //
  // Re-resolve reg.waiting fresh at CLICK time rather than trusting the
  // worker object the offer captured (issue 440, defense in depth alongside
  // the banner refresh below): if a second deploy superseded this offer, the
  // browser already discarded that worker (state -> redundant) and posting
  // to it does nothing — no takeover, no reload, a dead banner. Falling back
  // to the captured worker only covers the edge where the registration has
  // nothing waiting at all (e.g. it already activated between offer and
  // click), so the click still does its best effort.
  const worker = reg.waiting ?? offered;
  worker.postMessage({ type: 'SKIP_WAITING' });
}

type UpdateHooks = {
  reload?: () => void;
  versionTimeoutMs?: number;
};

export function watchForUpdate(reg: ServiceWorkerRegistration, hooks: UpdateHooks = {}): void {
  const reload = hooks.reload ?? ((): void => location.reload());
  const versionTimeoutMs = hooks.versionTimeoutMs ?? VERSION_REPLY_TIMEOUT_MS;

  hadController = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // The first-install claim: the page just became controlled, nothing was
    // replaced, nothing to reload. Any later change is a real takeover.
    if (!hadController) {
      hadController = true;
      return;
    }
    if (reloading) return;
    reloading = true;
    reload();
  });

  // Guards the async race in offer(): a second deploy's offer can start (and
  // resolve its versionOf() lookup) before an earlier offer's own lookup
  // finishes. Without this, the earlier reply could land last and repaint the
  // banner back to the OLDER version right after the newer one showed
  // (issue 440, AC.3). Each offer() call claims the next sequence number; a
  // resolving lookup only applies if it is still the latest one requested.
  let offerSeq = 0;

  const offer = (worker: ServiceWorker | null): void => {
    if (!worker) return;
    const seq = ++offerSeq;
    void versionOf(worker, versionTimeoutMs).then((version) => {
      if (seq !== offerSeq) return; // superseded by a newer offer while we waited
      updatePrompt.show(() => takeOver(reg, worker), version ?? undefined);
    });
  };

  // Already waiting when we got here. A tab opened after a deploy finds the new
  // worker installed and parked, and `updatefound` has long since fired — with
  // only the listener below, that tab would never hear about it.
  offer(reg.waiting);

  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      // `installed` with an existing controller means an UPDATE (there is an old
      // version running to replace). Without a controller it is the very first
      // install on this device, which needs no prompt — nothing is being replaced.
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        offer(reg.waiting ?? installing);
      }
    });
  });

  // Desk tabs stay open for days, and the browser only re-checks for a new
  // worker every ~24h on its own, so a deploy could go unnoticed for a day.
  // Coming back to the tab is the natural moment to look.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void reg.update().catch(() => undefined);
  });
}
